/**
 * @file: utils.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { kScreenContentPadding } from '@euler/app/tv/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { PixelScaleFn } from '@euler/functions/responsive/utils';
import { TaskBookmark } from '@euler/model';
import { VehicleInspectionTask } from '@euler/model/entity';
import { TaskDetail } from '@euler/model/task-detail';
import { StackNavigationOptions } from '@react-navigation/stack';
import * as Device from 'expo-device';
import { useMemo } from 'react';
import { Dimensions, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kDesignWidth = 1920;
const kDesignHeight = 1080;

let { width: kScreenWidth, height: kScreenHeight } = Dimensions.get('window');

const kScale = Device.deviceName?.includes('iPad') ? 0.8 : 1;

Dimensions.addEventListener('change', ({ window }) => {
  kScreenWidth = window.width;
  kScreenHeight = window.height;
});

export const taskKeyExtractor = (task: VehicleInspectionTask) => task.taskNo;

export const rpxHub: PixelScaleFn = (px, options) => {
  px = (px * kScreenHeight * kScale) / kDesignHeight;

  if (!options?.round) {
    return px;
  }

  return options.round === true || options.round === 'round'
    ? Math.round(px)
    : options.round === 'ceil'
    ? Math.ceil(px)
    : Math.floor(px);
};

export const rpxHubWeb: PixelScaleFn = (px, options) => {
  px = px * 0.65;

  if (!options?.round) {
    return px;
  }

  return options.round === true || options.round === 'round'
    ? Math.round(px)
    : options.round === 'ceil'
    ? Math.ceil(px)
    : Math.floor(px);
};

export const rpx_scale_factor_x = () => (kScreenWidth * kScale) / kDesignWidth;
export const rpx_scale_factor_y = () =>
  (kScreenHeight * kScale) / kDesignHeight;

export const printScreenInfo = () => {
  const screen = Dimensions.get('screen');
  __DEV__ &&
    console.log(
      `[${Device.designName}]:`,
      'screen.size:',
      [screen.width, screen.height].join('x'),
      'screen.resolution:',
      `${[screen.width, screen.height].map(x => x * screen.scale).join('x')}`,
      'rpx_scale_factor:',
      rpx_scale_factor_y(),
    );
};

export const tvModalScreenOptions: StackNavigationOptions = {
  presentation: 'transparentModal',
  animationEnabled: false,
  headerShown: false,
  cardStyle: {
    backgroundColor: 'transparent',
  },
};

export const taskDetailToBookmark = (detail: TaskDetail): TaskBookmark => {
  const { basicInfo } = detail;
  return {
    id: basicInfo.taskNo,
    taskNo: basicInfo.taskNo,
    licensePlateNo: basicInfo.licensePlateNo,
    vin: basicInfo.vin,
    vehicleName: basicInfo.vehicleName,
    vehicleBrandName: basicInfo.vehicleBrandName,
    vehicleBrandLogo: basicInfo.vehicleBrandLogo,
    vehicleModel: basicInfo.vehicleModel,
    vehicleModelYear: basicInfo.vehicleModelYear,
    vehicleMileage: basicInfo.vehicleMileage,
    serviceAgentName: basicInfo.serviceAgentName,
    vehicleImageUrl: basicInfo.vehicleImageUrl,
    createdAt: new Date(),
  };
};

export const useScreenContentPadding = () => {
  const insets = useSafeAreaInsets();
  const { rpx } = useResponsiveContext();
  return [
    Math.max(insets.left, rpx(kScreenContentPadding)),
    Math.max(insets.right, rpx(kScreenContentPadding)),
    insets,
  ] as const;
};

export const useScreenContentPaddingStyle = () => {
  const [left, right] = useScreenContentPadding();
  return useMemo<ViewStyle>(
    () => ({
      paddingLeft: left,
      paddingRight: right,
    }),
    [left, right],
  );
};

export const useScreenContentMarginStyle = () => {
  const [left, right] = useScreenContentPadding();
  return useMemo<ViewStyle>(
    () => ({
      marginLeft: left,
      marginRight: right,
    }),
    [left, right],
  );
};
